import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentDuplicateIcon, PlusIcon } from "@heroicons/react/24/outline";
import { User } from "../../../../../api/accessControl/User.ts";
import { BackendApi } from "../../../../../api/api.ts";
import { fetchDocumentContentBlob } from "../../../../../api/fetchDocumentContent.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import { PrimaryButton } from "../../../../../components/general/PrimaryButton.tsx";
import { SecondaryButton } from "../../../../../components/general/SecondaryButton.tsx";
import { useNotifications } from "../../../../../lib/NotificationContext.tsx";
import { SalesInvoicesOverview } from "../../components/salesInvoices/SalesInvoicesOverview.tsx";
import type {
    SalesInvoiceRow,
    SalesInvoicesSummary,
} from "../../components/salesInvoices/types/salesInvoice.ts";

export default function OutstandingSalesInvoicesPage({
    currentUser,
}: {
    currentUser: User;
}) {
    const [invoices, setInvoices] = useState<SalesInvoiceRow[]>([]);
    const [summary, setSummary] = useState<SalesInvoicesSummary | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const api = new BackendApi(currentUser.getToken());
    const { dispatch } = useNotifications();
    const navigate = useNavigate();

    async function onLoad() {
        try {
            const data = await api.Me__business__getSalesInvoices({});
            setInvoices(data.invoices as SalesInvoiceRow[]);
            setSummary(data.summary as SalesInvoicesSummary);
        } catch (e) {
            console.error(e);
            throw e;
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        onLoad();
    }, [currentUser]);

    async function handleFetchDocument(invoice: SalesInvoiceRow) {
        if (!invoice.document_id) return null;
        try {
            const blob = await fetchDocumentContentBlob(api, "entrepreneur/get-document-content", {
                document_id: invoice.document_id,
            });
            return {
                file_name: invoice.file_name ?? invoice.invoice_number ?? "factuur",
                file_blob: blob,
                mimetype: blob.type,
            };
        } catch (e) {
            console.error(e);
            dispatch({
                title: "Fout opgetreden",
                description: "Het document kon niet worden geladen.",
                type: "error",
            });
            return null;
        }
    }

    async function handleReportIncorrect(invoice: SalesInvoiceRow) {
        if (!invoice.document_id) return;
        try {
            await api.Entrepreneur__documents__reportIncorrect({ document_id: invoice.document_id });
            dispatch({
                title: "Doorgegeven",
                description: "We hebben je melding ontvangen en nemen contact op indien nodig.",
                type: "success",
            });
        } catch (e) {
            console.error(e);
            dispatch({
                title: "Fout opgetreden",
                description: "De melding kon niet worden verzonden. Probeer het later opnieuw.",
                type: "error",
            });
        }
    }

    return (
        <Shell
            currentUser={currentUser}
            title="Facturen"
            subtitle="Overzicht van al je verstuurde en betaalde facturen."
            titleActions={
                <div className="flex items-center gap-3">
                    <SecondaryButton
                        title="Concepten"
                        icon={DocumentDuplicateIcon}
                        onClick={() => navigate("/invoices/drafts")}
                    />
                    <PrimaryButton
                        title="Nieuwe factuur"
                        icon={PlusIcon}
                        onClick={() => navigate("/create-invoice")}
                    />
                </div>
            }
        >
            <SalesInvoicesOverview
                invoices={invoices}
                summary={summary}
                isLoading={isLoading}
                onFetchDocument={handleFetchDocument}
                onReportIncorrect={handleReportIncorrect}
                onOpenInvoice={(invoice) => navigate(`/invoices/${invoice.id}`)}
            />
        </Shell>
    );
}
